import type { CRMUser } from '../types';
import type {
  OrgChartNode,
  OrgChartPerson,
  OrgDepartment,
  OrgLevel,
  OrgSeat,
} from '../types/purchasing';
import { ORG_DEPARTMENTS, ORG_LEVELS } from '../types/purchasing';
import { isCrmTestUser, isCrmWorkstationUser } from './userRoles';

/** Internal W-2 and 1099 roles that appear on the chart. Designers, architects, engineers, suppliers, and clients do not. */
const ORG_ROLES = new Set(['employee', 'employee_shop', 'employee_office', 'admin', 'staff']);

export const ORG_LEVEL_LABELS: Record<OrgLevel, string> = {
  executive: 'Executive',
  managerial: 'Managerial',
  general: 'Staff',
};

/** Layout lane below Staff for CRM test bots. Not a saved `orgLevel`. */
export type OrgChartLane = OrgLevel | 'bots';

export const ORG_CHART_LANES: OrgChartLane[] = ['executive', 'managerial', 'general', 'bots'];

export const ORG_CHART_LANE_LABELS: Record<OrgChartLane, string> = {
  ...ORG_LEVEL_LABELS,
  bots: 'Bot Caste',
};

export const ORG_DEPARTMENT_LABELS: Record<OrgDepartment, string> = {
  sales: 'Sales',
  office: 'Office',
  shop: 'Shop',
};

export const ORG_SEAT_LABELS: Record<OrgSeat, string> = {
  ceo: 'CEO',
  coo: 'COO',
  cfo: 'CFO',
  office_manager: 'Office Manager',
  contractor: '1099',
  staff: '',
};

export const ORG_LEVEL_RANK: Record<OrgLevel, number> = {
  executive: 0,
  managerial: 1,
  general: 2,
};

const PINNED_SEATS = new Set<OrgSeat>(['ceo', 'coo', 'cfo', 'office_manager', 'contractor']);

/** Pre-rebuild rows: Executive / Office / Shop. */
const LEGACY_LEVELS: Record<string, OrgLevel> = {
  office: 'general',
  shop: 'general',
};

/** Pre-rebuild columns: Sales / Back Office / Operations. */
const LEGACY_DEPARTMENTS: Record<string, OrgDepartment> = {
  back_office: 'office',
  operations: 'shop',
};

export function parseOrgLevel(value: unknown): OrgLevel | undefined {
  if (typeof value !== 'string') return undefined;
  if ((ORG_LEVELS as string[]).includes(value)) return value as OrgLevel;
  return LEGACY_LEVELS[value];
}

export function parseOrgDepartment(value: unknown): OrgDepartment | undefined {
  if (typeof value !== 'string') return undefined;
  if ((ORG_DEPARTMENTS as string[]).includes(value)) return value as OrgDepartment;
  return LEGACY_DEPARTMENTS[value];
}

/** CEO / president sit at the apex of the chart regardless of group. */
export function isOrgChartApex(person: Pick<OrgChartPerson, 'title'> & { executiveBriefingProfile?: 'ceo' | 'coo' | null }): boolean {
  if (person.executiveBriefingProfile === 'ceo') return true;
  return /\b(ceo|chief executive|president)\b/i.test(String(person.title || ''));
}

export type OrgSeatInput = {
  title?: string;
  role?: string;
  designations?: string[];
  is1099Contractor?: boolean;
  executiveBriefingProfile?: 'ceo' | 'coo' | null;
  orgSeat?: OrgSeat;
};

/** Seat from title, designations, and the 1099 flag. Designations win over titles. */
export function resolveOrgSeat(input: OrgSeatInput): OrgSeat {
  const designations = (input.designations || []).map(d => String(d).toLowerCase());
  const title = String(input.title || '');
  if (isOrgChartApex({ title, executiveBriefingProfile: input.executiveBriefingProfile })) return 'ceo';
  if (
    designations.includes('coo')
    || input.executiveBriefingProfile === 'coo'
    || /\b(coo|chief operating)\b/i.test(title)
  ) {
    return 'coo';
  }
  if (designations.includes('cfo') || /\b(cfo|chief financial)\b/i.test(title)) return 'cfo';
  if (designations.includes('office_manager')) return 'office_manager';
  if (input.is1099Contractor === true) return 'contractor';
  return 'staff';
}

export function orgSeatOf(person: OrgSeatInput): OrgSeat {
  return person.orgSeat || resolveOrgSeat(person);
}

export function isPinnedSeat(seat: OrgSeat): boolean {
  return PINNED_SEATS.has(seat);
}

export function isOrgChartBot(
  person: { isTestUser?: boolean; displayName?: string } | null | undefined,
): boolean {
  if (!person) return false;
  if (person.isTestUser === true) return true;
  return /\bbot\b/i.test(String(person.displayName || ''));
}

export function isOrgChartPinned(person: OrgSeatInput & {
  pinned?: boolean;
  isTestUser?: boolean;
  displayName?: string;
}): boolean {
  if (isOrgChartBot(person)) return true;
  if (typeof person.pinned === 'boolean') return person.pinned;
  return isPinnedSeat(orgSeatOf(person));
}

/** Why a card cannot be moved. */
export function pinnedSeatReason(seat: OrgSeat): string {
  switch (seat) {
    case 'ceo': return 'Company apex — set by the CEO title.';
    case 'coo': return 'Pinned to Executive / Shop by the COO designation. Reports to the CEO.';
    case 'cfo': return 'Pinned to Executive / Office by the CFO designation. Reports to the CEO.';
    case 'office_manager': return 'Pinned to Managerial / Office by the Office Manager designation. Reports to the CEO.';
    case 'contractor': return 'In the 1099 contractor pool. Every contractor reports to the COO.';
    default: return '';
  }
}

export function pinnedBotReason(): string {
  return 'CRM test bot — pinned in the Bot Caste below Staff. The caste reports to the CEO.';
}

/** Default cell when `orgLevel` / `orgDepartment` have not been saved yet, or for pinned seats always. */
export function seedOrgPlacement(input: OrgSeatInput): { orgLevel: OrgLevel; orgDepartment: OrgDepartment } {
  const designations = (input.designations || []).map(d => String(d).toLowerCase());
  const title = String(input.title || '');
  const role = String(input.role || '').toLowerCase();

  switch (resolveOrgSeat(input)) {
    case 'ceo': return { orgLevel: 'executive', orgDepartment: 'office' };
    case 'coo': return { orgLevel: 'executive', orgDepartment: 'shop' };
    case 'cfo': return { orgLevel: 'executive', orgDepartment: 'office' };
    case 'office_manager': return { orgLevel: 'managerial', orgDepartment: 'office' };
    case 'contractor': return { orgLevel: 'general', orgDepartment: 'shop' };
    default: break;
  }

  if (designations.includes('sales_rep') || /\bsales\b/i.test(title)) {
    return { orgLevel: 'general', orgDepartment: 'sales' };
  }
  if (
    role === 'employee_shop'
    || /\b(shop|carpenter|timber|installer|install|field|framer|raise|crew)\b/i.test(title)
  ) {
    return { orgLevel: 'general', orgDepartment: 'shop' };
  }
  return { orgLevel: 'general', orgDepartment: 'office' };
}

/**
 * Pinned seats ignore any saved placement. Staff keep their saved (normalized)
 * placement, except that only designated executives may sit in the Executive layer.
 */
export function resolveOrgPlacement(person: OrgSeatInput & {
  orgLevel?: OrgLevel;
  orgDepartment?: OrgDepartment;
}): { orgLevel: OrgLevel; orgDepartment: OrgDepartment } {
  const seeded = seedOrgPlacement(person);
  if (isPinnedSeat(orgSeatOf(person))) return seeded;
  const savedLevel = parseOrgLevel(person.orgLevel);
  const savedDept = parseOrgDepartment(person.orgDepartment);
  return {
    orgLevel: savedLevel && savedLevel !== 'executive' ? savedLevel : seeded.orgLevel,
    orgDepartment: savedDept || seeded.orgDepartment,
  };
}

export function isOrgChartPerson(user: Pick<CRMUser, 'role' | 'isActive' | 'isTestUser' | 'isWorkstation'>): boolean {
  return user.isActive !== false
    && !isCrmWorkstationUser(user)
    && ORG_ROLES.has(String(user.role || '').toLowerCase());
}

/**
 * Chart people without effective managers. Run `applyEffectiveReporting`
 * (orgReporting.ts) before drawing edges or diffing against Firestore.
 */
export function crmUsersToOrgPeople(crmUsers: CRMUser[]): OrgChartPerson[] {
  return crmUsers.filter(isOrgChartPerson).map(u => {
    const briefing = u.executiveBriefing;
    const executiveBriefingProfile = briefing?.enabled ? briefing.profile : briefing?.profile || null;
    const base: OrgSeatInput = {
      title: u.title,
      role: u.role,
      designations: u.designations,
      is1099Contractor: u.is1099Contractor === true,
      executiveBriefingProfile,
    };
    const orgSeat = resolveOrgSeat(base);
    const place = resolveOrgPlacement({
      ...base,
      orgSeat,
      orgLevel: parseOrgLevel(u.orgLevel),
      orgDepartment: parseOrgDepartment(u.orgDepartment),
    });
    return {
      uid: u.uid,
      displayName: (u.displayName || u.email || 'Unnamed').trim(),
      title: u.title,
      photoURL: u.photoURL,
      role: u.role,
      isActive: u.isActive !== false,
      reportsToUserId: u.reportsToUserId?.trim() || undefined,
      designations: u.designations,
      is1099Contractor: u.is1099Contractor === true,
      isTestUser: isCrmTestUser(u),
      executiveBriefingProfile,
      orgSeat,
      pinned: isOrgChartPinned({
        ...base,
        orgSeat,
        isTestUser: isCrmTestUser(u),
        displayName: (u.displayName || u.email || 'Unnamed').trim(),
      }),
      orgLevel: place.orgLevel,
      orgDepartment: place.orgDepartment,
    };
  });
}

/** Manager the chart draws: effective (rule-derived) when present, else the stored value. */
export function chartManagerId(person: Pick<OrgChartPerson, 'reportsToUserId' | 'effectiveReportsToUserId'>): string | undefined {
  if ('effectiveReportsToUserId' in person && person.effectiveReportsToUserId !== undefined) {
    return person.effectiveReportsToUserId || undefined;
  }
  return person.reportsToUserId || undefined;
}

export function reportsToMap(
  people: Pick<OrgChartPerson, 'uid' | 'reportsToUserId' | 'effectiveReportsToUserId'>[],
): Record<string, string | undefined> {
  const map: Record<string, string | undefined> = {};
  for (const p of people) map[p.uid] = chartManagerId(p);
  return map;
}

/** True if assigning `newManagerId` as manager of `userId` would create a cycle. */
export function wouldCreateReportingCycle(
  reportsToByUserId: Record<string, string | undefined>,
  userId: string,
  newManagerId: string | null | undefined,
): boolean {
  if (!newManagerId) return false;
  if (newManagerId === userId) return true;
  const seen = new Set<string>([userId]);
  let cur: string | undefined = newManagerId;
  while (cur) {
    if (seen.has(cur)) return true;
    seen.add(cur);
    cur = reportsToByUserId[cur];
  }
  return false;
}

export function buildOrgForest(people: OrgChartPerson[]): OrgChartNode[] {
  const byId = new Map(people.map(p => [p.uid, p]));
  const childIds = new Map<string, string[]>();
  const rootIds: string[] = [];
  const map = reportsToMap(people);

  for (const p of people) {
    const mgr = chartManagerId(p);
    if (mgr && byId.has(mgr) && !wouldCreateReportingCycle({ ...map, [p.uid]: undefined }, p.uid, mgr)) {
      const list = childIds.get(mgr) || [];
      list.push(p.uid);
      childIds.set(mgr, list);
    } else {
      rootIds.push(p.uid);
    }
  }

  const sortIds = (ids: string[]) =>
    ids.slice().sort((a, b) => {
      const an = byId.get(a)?.displayName || '';
      const bn = byId.get(b)?.displayName || '';
      return an.localeCompare(bn);
    });

  const walk = (uid: string, trail: Set<string>): OrgChartNode | null => {
    const person = byId.get(uid);
    if (!person || trail.has(uid)) return null;
    const next = new Set(trail);
    next.add(uid);
    const children = sortIds(childIds.get(uid) || [])
      .map(id => walk(id, next))
      .filter((n): n is OrgChartNode => !!n);
    return { person, children };
  };

  return sortIds(rootIds)
    .map(id => walk(id, new Set()))
    .filter((n): n is OrgChartNode => !!n);
}
